var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// libs
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
// components
import { KeywordSelect } from '@/pages/instamart/campaign/create/widgets/Steps/Keywords/components/KeywordSelect';
import { KeywordTable } from '@/pages/instamart/campaign/create/widgets/Steps/Keywords/components/KeywordTable';
// types
import { KeywordMatchType } from '@local/typedefs/dist/protos/campaign/Criteria';
import { AdUnitType, } from '@local/typedefs/dist/shaped/campaign/Campaign';
// helpers
import { checkIfKeywordExists, checkIfKeywordAlreadySelected, fetchSuggestedKeywordsHelper, fetchSelectedSuggestedKeywordsHelper, } from './helpers';
// hooks
import useSuggestedKeywordsStore from '@/stores/SuggestedKeywords';
import useWriteCampaignStore from '@/stores/WriteCampaign';
import useAccountStore from '@/stores/account';
import usePrevious from '@/hooks/usePrevious';
// includes
import { fetchSuggestedKeywordsApi } from '@/includes/api/keywords/suggestions';
import { AD_GROUP_INDEX } from '@/constants/campaign';
// styles
import styles from './Keywords.module.scss';
function Keywords({ mode, currentCampaign }) {
    var _a;
    const [loading, setLoading] = useState(false);
    // store hooks
    const { suggestedKeywords, customSuggestedKeywords, setSuggestedKeywords, setCustomSuggestedKeywords, appendCustomSuggestedKeywords, } = useSuggestedKeywordsStore((store) => ({
        suggestedKeywords: store.keywords,
        customSuggestedKeywords: store.customKeywords,
        appendCustomSuggestedKeywords: store.appendCustomKeywords,
        setSuggestedKeywords: store.setKeywords,
        setCustomSuggestedKeywords: store.setCustomKeywords,
    }));
    const { selectedKeywords, selectedCandidates, addSelectedKeyword, updateKeywordByIndex, deleteKeywordByIndex, placementIds, validationMessages, isValid, } = useWriteCampaignStore((store) => ({
        selectedKeywords: store.adGroups[AD_GROUP_INDEX].keywords,
        selectedCandidates: store.adGroups[AD_GROUP_INDEX].candidates,
        addSelectedKeyword: store.addAdGroupKeyword,
        updateKeywordByIndex: store.updateKeywordByIndex,
        deleteKeywordByIndex: store.deleteKeywordByIndex,
        placementIds: store.placementIds,
        validationMessages: store.validationMessages,
        isValid: store.isValid,
    }));
    const getCurrentAccountId = useAccountStore((state) => state.getCurrentAccountId);
    const allSuggestedKeywords = useMemo(() => {
        return [...suggestedKeywords, ...customSuggestedKeywords];
    }, [suggestedKeywords, customSuggestedKeywords]);
    const preSelectedKeywords = (_a = currentCampaign === null || currentCampaign === void 0 ? void 0 : currentCampaign.adGroups[0].keywords) !== null && _a !== void 0 ? _a : [];
    const prevKeywordLength = usePrevious(selectedKeywords.length);
    const tableRef = React.useRef(null);
    /**
     * for scrolling to the last keyword added
     */
    useEffect(() => {
        if (tableRef.current &&
            typeof prevKeywordLength === 'number' &&
            selectedKeywords.length > prevKeywordLength) {
            const lastRowEl = tableRef.current.lastElementChild;
            lastRowEl && lastRowEl.scrollIntoView({ behavior: 'smooth' });
        }
    }, [selectedKeywords]);
    useEffect(() => {
        !loading && fetchSuggestedKeywords();
    }, []);
    useEffect(() => {
        isValid(['keywords'], { keywords: allSuggestedKeywords });
    }, [allSuggestedKeywords]);
    const fetchSuggestedKeywords = () => __awaiter(this, void 0, void 0, function* () {
        try {
            setLoading(true);
            const accountId = yield getCurrentAccountId();
            const candidateIds = selectedCandidates.map((candidates) => candidates.id);
            const suggestedKeywordsResult = yield fetchSuggestedKeywordsHelper({
                candidateIds,
                placementIds,
                adUnitType: AdUnitType.PRODUCT,
                accountId,
            });
            const selectedSuggestedKeywordResult = yield fetchSelectedSuggestedKeywordsHelper({
                suggestedKeywords: suggestedKeywordsResult,
                selectedKeywords,
                candidateIds,
                placementIds,
                adUnitType: AdUnitType.PRODUCT,
                accountId,
                mode: mode,
                preSelectedKeywords,
            });
            setSuggestedKeywords(suggestedKeywordsResult);
            setCustomSuggestedKeywords(selectedSuggestedKeywordResult);
        }
        catch (err) {
            // TODO: error handling
            setSuggestedKeywords([]);
        }
        finally {
            setLoading(false);
        }
    });
    const filteredSuggestedKeywords = useMemo(() => {
        return suggestedKeywords.filter((suggestedKeyword) => !selectedKeywords.find((selectedKeyord) => selectedKeyord.keywordText ===
            suggestedKeyword.keywordText));
    }, [selectedKeywords, suggestedKeywords]);
    const fetchCustomSuggestedKeyword = (keywordText) => __awaiter(this, void 0, void 0, function* () {
        try {
            const parsedKeywordText = keywordText.trim().toLowerCase();
            const accountId = yield getCurrentAccountId();
            const suggestedKeywords = yield fetchSuggestedKeywordsApi({
                candidateIds: selectedCandidates.map((candidates) => candidates.id),
                placementIds,
                adUnitType: AdUnitType.PRODUCT,
                keywords: [parsedKeywordText],
                accountId,
            });
            if (Array.isArray(suggestedKeywords) && suggestedKeywords.length) {
                appendCustomSuggestedKeywords(suggestedKeywords);
                return suggestedKeywords[0];
            }
        }
        catch (err) {
            // TODO: error handling
        }
    });
    const handleKeywordSelect = useCallback((suggestedKeyword) => {
        const keyword = {
            keywordText: suggestedKeyword.keywordText,
            matchType: KeywordMatchType.KEYWORD_MATCH_TYPE_BROAD,
            bid: suggestedKeyword[KeywordMatchType.KEYWORD_MATCH_TYPE_BROAD]
                .bid.minimumBid,
        };
        addSelectedKeyword(keyword, AD_GROUP_INDEX);
        isValid(['keywords'], {
            keywords: allSuggestedKeywords.concat(suggestedKeyword),
        });
    }, [addSelectedKeyword, allSuggestedKeywords]);
    const handleKeywordAdd = (suggestedKeyword) => {
        addSelectedKeyword({
            keywordText: suggestedKeyword.keywordText,
            matchType: KeywordMatchType.KEYWORD_MATCH_TYPE_BROAD,
            bid: suggestedKeyword[KeywordMatchType.KEYWORD_MATCH_TYPE_BROAD]
                .bid.minimumBid,
        }, AD_GROUP_INDEX);
        isValid(['keywords'], {
            keywords: allSuggestedKeywords.concat(suggestedKeyword),
        });
    };
    const handleKeywordAddInputBox = useCallback((keywordText) => __awaiter(this, void 0, void 0, function* () {
        const parsedKeywordText = keywordText.trim().toLowerCase();
        const isKeywordAlreadySelected = checkIfKeywordAlreadySelected(selectedKeywords, parsedKeywordText);
        if (isKeywordAlreadySelected) {
            return;
        }
        const existingKeyword = checkIfKeywordExists([...filteredSuggestedKeywords, ...customSuggestedKeywords], parsedKeywordText);
        if (existingKeyword) {
            handleKeywordAdd(existingKeyword);
            return;
        }
        // get keyword data from API and add it to selected keywords
        const customSuggestedKeyword = yield fetchCustomSuggestedKeyword(parsedKeywordText);
        customSuggestedKeyword && handleKeywordAdd(customSuggestedKeyword);
    }), [addSelectedKeyword, selectedKeywords, filteredSuggestedKeywords]);
    const handleKeywordUpdate = useCallback((changes, keywordIndex) => {
        updateKeywordByIndex(changes, keywordIndex, AD_GROUP_INDEX);
        isValid(['keywords'], { keywords: allSuggestedKeywords });
    }, [updateKeywordByIndex, isValid, allSuggestedKeywords]);
    const handleRetryFetchSuggestedKeywords = () => __awaiter(this, void 0, void 0, function* () {
        return !loading && fetchSuggestedKeywords();
    });
    return (React.createElement("div", null,
        React.createElement("div", { className: cx(styles.tile, styles.tileForKeywordSelect) },
            React.createElement(KeywordSelect, { loading: loading, retryFetchSuggestedKeywords: handleRetryFetchSuggestedKeywords, suggestedKeywords: filteredSuggestedKeywords, onAddKeyword: handleKeywordAddInputBox, onSelectKeyword: handleKeywordSelect, showRetry: suggestedKeywords.length === 0 })),
        React.createElement("div", { className: cx(styles.tile, styles.tileForKeywordTable) },
            React.createElement(KeywordTable, { mode: mode, suggestedKeywords: allSuggestedKeywords, preSelectedKeywords: preSelectedKeywords, selectedKeywords: selectedKeywords, updateKeywordByIndex: handleKeywordUpdate, deleteKeywordByIndex: deleteKeywordByIndex, keywordErrorMessages: validationMessages['keywords'], tableRef: tableRef }))));
}
export default React.memo(Keywords);
